import { generateClient } from 'aws-amplify/api';

import { fetchAuthSession } from 'aws-amplify/auth';
import { Amplify } from 'aws-amplify';
import { initializeAmplify } from 'src/utils/auth';
import { GeneratedSubscription } from '@aws-amplify/api-graphql';
import { store } from 'src/main';

type OnUpdateDevContentGenerationRequestsSubscription = {
  onUpdateDevContentGenerationRequests: {
    beaconClientId: string;
    status: string;
    skuContentGenerationId: string;
    profileName: string;
    profileId: string;
    profileVersion?: number;
    updatedAt: string;
    retailerSku: string;
    projectId?: string;
    createdBy?: string;
    version?: number;
  };
};

const subscriptionQuery = `subscription OnUpdateDevContentGenerationRequests($beaconClientId: Int!) {
  onUpdateDevContentGenerationRequests(beaconClientId: $beaconClientId) {
    beaconClientId
    status
    skuContentGenerationId
    profileName
    profileId
    profileVersion
    updatedAt
    retailerSku
    projectId
    createdBy
    version
  }
}` as GeneratedSubscription<{ beaconClientId: number }, OnUpdateDevContentGenerationRequestsSubscription>;

type OnContentGenerationProjectEventSubscription = {
  onContentGenerationProjectEvent: {
    projectId: string;
    name: string;
    beaconClientId: number;
    createdAt: string;
    updatedAt: string;
    createdBy: string;
    updatedBy: string;
  };
};

const projectSubscriptionQuery = `subscription OnContentGenerationProjectEvent($beaconClientId: Int!) {
  onContentGenerationProjectEvent(beaconClientId: $beaconClientId) {
    projectId
    name
    beaconClientId
    createdAt
    updatedAt
    createdBy
    updatedBy
  }
}` as GeneratedSubscription<{ beaconClientId: number }, OnContentGenerationProjectEventSubscription>;

interface SubscribeToUpdatesProps {
  onMessage: (data) => void;
  onError: (error) => void;
}

// Initialize Amplify and create GraphQL client once
initializeAmplify();
const config = Amplify.getConfig();
const graphqlClient = generateClient({ endpoint: config.API.GraphQL.endpoint, authMode: 'lambda' });

// Keep track of the current subscription to avoid multiple active subscriptions
let currentSubscription: { unsubscribe: () => void } | null = null;
let currentProjectSubscription: { unsubscribe: () => void } | null = null;

export const subscribeToUpdates = async ({ onMessage, onError }: SubscribeToUpdatesProps) => {
  const rawBeaconClientId = store.getState().user.config?.vendor?.BeaconClientId;

  if (!rawBeaconClientId) {
    onError(new Error('Beacon client ID not found'));
    return { unsubscribe: () => { } };
  }

  const beaconClientId = typeof rawBeaconClientId === 'number' ? rawBeaconClientId : Number(rawBeaconClientId);

  try {
    const { tokens } = await fetchAuthSession();
    const token = tokens?.idToken?.toString();

    const subscription = graphqlClient.graphql({
      query: subscriptionQuery,
      variables: { beaconClientId },
      authMode: 'lambda',
      authToken: token
    });

    // Store the actual subscription object returned by subscribe()
    const subscriptionObj = subscription.subscribe({
      next: (response) => {
        onMessage(response.data.onUpdateDevContentGenerationRequests);
      },
      error: (error) => {
        console.error('Subscription error:', error);
        onError(error);
      }
    });

    // Store the subscription reference with proper unsubscribe
    currentSubscription = {
      unsubscribe: () => {
        subscriptionObj.unsubscribe();
        currentSubscription = null;
      }
    };

    return currentSubscription;
  } catch (error) {
    console.error('Error setting up subscription:', error);
    onError(error);
    return { unsubscribe: () => { } };
  }
};

export const subscribeToProjectEvents = async ({ onMessage, onError }: SubscribeToUpdatesProps) => {
  const rawBeaconClientId = store.getState().user.config?.vendor?.BeaconClientId;

  if (!rawBeaconClientId) {
    onError(new Error('Beacon client ID not found'));
    return { unsubscribe: () => { } };
  }

  const beaconClientId = typeof rawBeaconClientId === 'number' ? rawBeaconClientId : Number(rawBeaconClientId);

  try {
    // Unsubscribe from existing subscription before creating a new one
    if (currentProjectSubscription) {
      currentProjectSubscription.unsubscribe();
    }

    const { tokens } = await fetchAuthSession();
    const token = tokens.idToken.toString();

    const subscription = graphqlClient.graphql({
      query: projectSubscriptionQuery,
      variables: { beaconClientId },
      authMode: 'lambda',
      authToken: token
    });

    // Store the actual subscription object returned by subscribe()
    const subscriptionObj = subscription.subscribe({
      next: (response) => {
        onMessage(response.data.onContentGenerationProjectEvent);
      },
      error: (error) => {
        console.error('Project subscription error:', error);
        onError(error);
      }
    });

    currentProjectSubscription = {
      unsubscribe: () => {
        subscriptionObj.unsubscribe();
        currentProjectSubscription = null;
      }
    };

    return currentProjectSubscription;
  } catch (error) {
    console.error('Error setting up project subscription:', error);
    onError(error);
    return { unsubscribe: () => { } };
  }
};
